import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { AlertCircle, Loader2, PlayCircle } from 'lucide-react';

/**
 * VideoPlayer - Componente unificado para MP4, HLS y embed (Fase 06A).
 *
 * Props:
 *   protocol: 'mp4' | 'hls' | 'embed'
 *   playbackUrl: URL directa (mp4/hls)
 *   embedUrl: URL del iframe (embed)
 *   poster: thumbnail (opcional)
 *   onError(err): callback de error
 */
export function VideoPlayer({ protocol, playbackUrl, embedUrl, poster, onError, testId }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canPlay, setCanPlay] = useState(false);

  // Si el "embed" apunta realmente a un MP4 directo de Bunny (*.b-cdn.net),
  // lo reproducimos con el elemento <video> protegido en vez de un iframe.
  // Mantiene la URL firmada tal cual y reutiliza controlsList y la
  // restricción de menú contextual existentes.
  const isDirectBunnyMp4 = (() => {
    const url = embedUrl || playbackUrl || '';
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      const path = (u.pathname || '').toLowerCase();
      return host.endsWith('.b-cdn.net') && /\.mp4$/.test(path);
    } catch { return false; }
  })();
  const effectiveProtocol = (protocol === 'embed' && isDirectBunnyMp4) ? 'mp4' : protocol;
  const effectivePlaybackUrl = (protocol === 'embed' && isDirectBunnyMp4) ? embedUrl : playbackUrl;

  useEffect(() => {
    setError(null);
    setLoading(true);
    setCanPlay(false);

    if (effectiveProtocol === 'embed') {
      setLoading(false);
      return undefined;
    }

    const v = videoRef.current;
    if (!v) return undefined;

    let cleanup = () => {};

    if (effectiveProtocol === 'mp4') {
      v.src = effectivePlaybackUrl;
      v.load();
      cleanup = () => {
        try { v.removeAttribute('src'); v.load(); } catch (_) { /* noop */ }
      };
    } else if (effectiveProtocol === 'hls') {
      // Soporte nativo HLS (Safari iOS / macOS)
      if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = effectivePlaybackUrl;
        v.load();
        cleanup = () => {
          try { v.removeAttribute('src'); v.load(); } catch (_) { /* noop */ }
        };
      } else if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
        hlsRef.current = hls;
        hls.attachMedia(v);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(effectivePlaybackUrl));
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) {
            setError({ kind: 'hls_fatal', detail: data.details });
            if (onError) onError(data);
          }
        });
        cleanup = () => { try { hls.destroy(); } catch (_) { /* noop */ } };
      } else {
        setError({ kind: 'hls_not_supported', detail: 'Tu navegador no soporta HLS.' });
        setLoading(false);
      }
    }

    const handleCanPlay = () => { setCanPlay(true); setLoading(false); };
    const handleError = () => {
      setError({ kind: 'mediaerror', detail: v.error?.message || 'Error de reproducción' });
      setLoading(false);
      if (onError) onError(v.error);
    };
    const handleWaiting = () => setLoading(true);
    const handlePlaying = () => setLoading(false);

    v.addEventListener('canplay', handleCanPlay);
    v.addEventListener('error', handleError);
    v.addEventListener('waiting', handleWaiting);
    v.addEventListener('playing', handlePlaying);

    return () => {
      v.removeEventListener('canplay', handleCanPlay);
      v.removeEventListener('error', handleError);
      v.removeEventListener('waiting', handleWaiting);
      v.removeEventListener('playing', handlePlaying);
      cleanup();
    };
  }, [effectiveProtocol, effectivePlaybackUrl, onError]);

  if (effectiveProtocol === 'embed') {
    return (
      <div
        data-testid={testId || 'video-player-embed'}
        className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden"
      >
        <iframe
          title="Reproductor embebido"
          src={embedUrl}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
    );
  }

  return (
    <div
      data-testid={testId || `video-player-${effectiveProtocol}`}
      className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-[0_18px_48px_rgba(28,22,18,0.18)]"
    >
      <video
        ref={videoRef}
        controls
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        playsInline
        preload="metadata"
        poster={poster || undefined}
        className="absolute inset-0 w-full h-full object-contain bg-black"
      />
      {loading && !error ? (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px] pointer-events-none"
          data-testid="video-player-loading"
        >
          <div className="inline-flex flex-col items-center gap-2 text-white">
            <Loader2 className="h-7 w-7 animate-spin text-[var(--ns-gold)]" />
            <span className="text-xs uppercase tracking-wider opacity-80">Cargando vídeo…</span>
          </div>
        </div>
      ) : null}
      {!loading && !canPlay && !error ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <PlayCircle className="h-16 w-16 text-white/80 drop-shadow" />
        </div>
      ) : null}
      {error ? (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/75 p-6 text-center"
          data-testid="video-player-error"
        >
          <div className="max-w-md text-white">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <h3 className="font-display text-lg mb-1">Error de reproducción</h3>
            <p className="text-sm text-white/70">{error.detail || 'No se ha podido cargar el vídeo.'}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
